import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Edit, Trash2, Eye, Plus, Clock, Check, X, Database, Shield, AlertCircle, Copy, Code, Camera, Loader } from '../../lib/lucide-proxy';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const BlogAdmin = () => {
  const { profile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isApiModalOpen, setIsApiModalOpen] = useState(false);
  
  const [selectedPost, setSelectedPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', category_id: '', tags: [], author: '', featured_image: '', is_published: false
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          category:blog_categories(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // ... (Rest of logic remains the same, skipping to Render for brevity in artifact, 
  // but in full file generation I must include everything. Since I am editing the file,
  // I will include the full content to ensure no logic is lost, focusing on the return/render fix)

  const handleImageUpload = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;
      const fileExt = file.name.split('.').pop();
      const fileName = `blog-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);
      setFormData(prev => ({ ...prev, featured_image: publicUrl }));
      toast.success('Imagem enviada com sucesso!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const postData = {
        ...formData,
        slug: formData.slug || formData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, ''),
        tags: formData.tags.filter(tag => tag.trim() !== '')
      };
      if (selectedPost) {
        const { error } = await supabase.from('blog_posts').update(postData).eq('id', selectedPost.id);
        if (error) throw error;
        toast.success('Post atualizado com sucesso!');
      } else {
        const { error } = await supabase.from('blog_posts').insert([postData]);
        if (error) throw error;
        toast.success('Post criado com sucesso!');
      }
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erro ao salvar post');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content || '',
      category_id: post.category_id || '',
      tags: post.tags || [],
      author: post.author || '',
      featured_image: post.featured_image || '',
      is_published: post.is_published
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (postId) => {
    if (!confirm('Tem certeza que deseja excluir este post?')) return;
    try {
      const { error } = await supabase.from('blog_posts').delete().eq('id', postId);
      if (error) throw error;
      toast.success('Post excluído com sucesso!');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erro ao excluir post');
    }
  };

  const handlePublish = async (postId, published) => {
    try {
      const { error } = await supabase.from('blog_posts').update({ is_published: published }).eq('id', postId);
      if (error) throw error;
      toast.success(published ? 'Post publicado!' : 'Post despublicado!');
      fetchPosts();
    } catch (error) {
      console.error('Error updating post:', error);
      toast.error('Erro ao atualizar post');
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(tag => tag !== tagToRemove) });
  };

  const resetForm = () => {
    setFormData({ title: '', slug: '', excerpt: '', content: '', category_id: '', tags: [], author: '', featured_image: '', is_published: false });
    setTagInput('');
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const getStatusBadge = (post) => {
    if (post.is_published) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Publicado</span>;
    } else {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Rascunho</span>;
    }
  };

  const rpcUrl = `https://cerveja.delivery/rest/v1/rpc/bulk_create_blog_posts`;
  const examplePayload = { "posts": [ { "title": "Meu Post", "content": "HTML...", "category": "Notícias" } ] };

  if (loading && posts.length === 0) return <div className="flex items-center justify-center h-64"><Loader className="animate-spin" /></div>;

  return (
    <>
      <Helmet><title>Gerenciar Blog - Cerveja Delivery</title></Helmet>
      
      {/* Container with max-width and internal spacing */}
      <div className="space-y-6 w-full max-w-[100vw] sm:max-w-full">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-dark-900">Gerenciar Blog</h2>
            <p className="text-dark-600">Crie, edite e publique conteúdo.</p>
          </div>
          <div className="flex space-x-3 w-full sm:w-auto">
            <Button variant="secondary" onClick={() => setIsApiModalOpen(true)} className="flex-1 sm:flex-none justify-center">
              <Database className="w-4 h-4 mr-2" /> API
            </Button>
            <Button variant="primary" onClick={() => setIsCreateModalOpen(true)} className="flex-1 sm:flex-none justify-center">
              <Plus className="w-4 h-4 mr-2" /> Novo
            </Button>
          </div>
        </div>

        {/* CSS FIX: w-full and overflow-hidden on the card container */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full overflow-hidden">
          {/* CSS FIX: overflow-x-auto on the table wrapper to allow scrolling inside the card */}
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-[800px]"> {/* min-w forces scroll on small screens */}
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoria</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Autor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 line-clamp-1">{post.title}</div>
                      <div className="text-sm text-gray-500 line-clamp-1">{post.excerpt?.substring(0, 40)}...</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className="text-sm text-gray-900">{post.category?.name}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{post.author}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(post)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                         {!post.is_published ? (
                          <button onClick={() => handlePublish(post.id, true)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                        ) : (
                          <button onClick={() => handlePublish(post.id, false)} className="p-1 text-orange-600 hover:bg-orange-50 rounded"><X className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => handleEdit(post)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(post.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {posts.length === 0 && <div className="text-center py-8 text-gray-500">Nenhum post encontrado.</div>}
          </div>
        </div>
      </div>

       {/* API Modal */}
       <Modal isOpen={isApiModalOpen} onClose={() => setIsApiModalOpen(false)} title="API de Importação" size="lg">
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded text-blue-800 text-sm">Use esta API para automações.</div>
          <div>
            <label className="text-xs font-bold">Endpoint</label>
            <div className="bg-gray-900 text-green-400 p-2 rounded text-xs break-all cursor-pointer" onClick={() => copyToClipboard(rpcUrl)}>{rpcUrl}</div>
          </div>
          <div>
            <label className="text-xs font-bold">JSON Body</label>
            <pre className="bg-gray-900 text-gray-300 p-2 rounded text-xs overflow-auto max-h-40">{JSON.stringify(examplePayload, null, 2)}</pre>
          </div>
          <div className="flex justify-end"><Button variant="secondary" onClick={() => setIsApiModalOpen(false)}>Fechar</Button></div>
        </div>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isCreateModalOpen || isEditModalOpen} onClose={() => { setIsCreateModalOpen(false); setIsEditModalOpen(false); resetForm(); }} title={selectedPost ? 'Editar Post' : 'Novo Post'} size="full">
        <form onSubmit={handleSubmit} className="space-y-4 bg-white p-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Título" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
            <Input label="Slug" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="Automático" />
          </div>
          <Input label="Resumo" value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Conteúdo (HTML)</label>
            <textarea className="input-field font-mono text-xs" rows={10} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
              <select value={formData.category_id} onChange={(e) => setFormData({ ...formData, category_id: e.target.value })} className="input-field">
                <option value="">Selecione...</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Input label="Autor" required value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} />
          </div>
          
          {/* Image Upload Simplified */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Imagem</label>
            <div className="flex gap-2">
              <label className="btn-secondary cursor-pointer flex items-center">{uploading ? <Loader className="animate-spin w-4 h-4" /> : <Camera className="w-4 h-4" />} Upload <input type="file" className="hidden" onChange={handleImageUpload} /></label>
              <input type="text" className="input-field flex-1" placeholder="URL da imagem" value={formData.featured_image} onChange={e => setFormData({...formData, featured_image: e.target.value})} />
            </div>
          </div>
          
          {/* Tags */}
          <div className="flex gap-2">
            <input type="text" className="input-field" value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Tag + Enter" onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTag())} />
            <Button type="button" onClick={addTag}>Add</Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {formData.tags.map(tag => <span key={tag} className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center">{tag} <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => removeTag(tag)} /></span>)}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setIsCreateModalOpen(false); setIsEditModalOpen(false); resetForm(); }}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={loading}>Salvar</Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default BlogAdmin;